"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import SeoHead from "@/components/SEO/SeoHead";
import BreadcrumbWidget from "@/components/Widgets/BreadcrumbWidget";
import RelatedContentWidget from "@/components/Widgets/RelatedContentWidget";
import SocialShareWidget from "@/components/Widgets/SocialShareWidget";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { generateBlogPostSchema, generateFAQSchema } from "@/utils/schemaGenerators";
import { trackEvent } from "@/utils/gtm";
import { whatsappMessages } from "@/utils/whatsapp";
import { FORM_CONTEXTS, openLeadPopup } from "@/utils/innovlistWidget";

const BLOG_CATEGORIES = [
  { label: "Career Guides", href: "/blog/category/career-guides" },
  { label: "Course Spotlights", href: "/blog/category/course-spotlights" },
  { label: "IT Industry Trends", href: "/blog/category/industry-trends" },
  { label: "Study Tips & Strategies", href: "/blog/category/study-tips" },
];

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function applyInlineFormatting(text: string) {
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-[#1A1A1A] px-1.5 py-0.5 rounded text-sm">$1</code>');
}

interface InternalLink {
  label?: string;
  slug?: string;
}

function injectInternalLinks(content: string, internalLinks?: InternalLink[]) {
  let output = content || "";

  (internalLinks || []).forEach((item) => {
    if (!item?.label || !item?.slug) return;

    const anchor = `<a href="${item.slug}" class="text-[#1B61FF] font-semibold hover:underline">${item.label}</a>`;
    const labelPattern = new RegExp(escapeRegExp(item.label), "g");
    output = output.replace(labelPattern, anchor);
  });

  return output;
}

function markdownToHtml(markdown: string) {
  const lines = (markdown || "").split("\n");
  const parts: string[] = [];
  let inList = false;

  const closeListIfOpen = () => {
    if (inList) {
      parts.push("</ul>");
      inList = false;
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      closeListIfOpen();
      return;
    }

    if (line.startsWith("### ")) {
      closeListIfOpen();
      parts.push(`<h3 class="text-[#1A1A1A] text-xl font-bold mt-10 mb-4">${applyInlineFormatting(line.slice(4))}</h3>`);
      return;
    }

    if (line.startsWith("## ")) {
      closeListIfOpen();
      parts.push(`<h2 class="text-[#1A1A1A] text-2xl font-bold mt-12 mb-5">${applyInlineFormatting(line.slice(3))}</h2>`);
      return;
    }

    if (line.startsWith("# ")) {
      closeListIfOpen();
      parts.push(`<h1 class="text-[#1A1A1A] text-3xl font-extrabold mt-12 mb-5">${applyInlineFormatting(line.slice(2))}</h1>`);
      return;
    }

    if (line.startsWith("- ")) {
      if (!inList) {
        parts.push('<ul class="list-disc pl-6 space-y-2 my-6 text-[#4B5563]">');
        inList = true;
      }
      parts.push(`<li>${applyInlineFormatting(line.slice(2))}</li>`);
      return;
    }

    closeListIfOpen();
    parts.push(`<p class="text-[#4B5563] text-[18px] leading-relaxed my-5">${applyInlineFormatting(line)}</p>`);
  });

  closeListIfOpen();
  return parts.join("\n");
}

function renderArticleHtml(content: string, internalLinks?: InternalLink[]) {
  const withLinks = injectInternalLinks(content, internalLinks);
  const hasHtml = /<[^>]+>/.test(withLinks);
  return hasHtml ? withLinks : markdownToHtml(withLinks);
}

function formatDisplayDate(value?: string) {
  if (!value) return null;

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
}

interface RelatedPost {
  title: string;
  slug: string;
  excerpt?: string;
}

export interface BlogPostData {
  seo?: {
    title?: string;
    description?: string;
    focusKeyword?: string;
  };
  hero?: {
    title?: string;
    category?: string;
    readTime?: string;
    image?: string;
    datePublished?: string;
  };
  content?: string;
  internalLinks?: InternalLink[];
  author?: string;
  datePublished?: string;
  dateModified?: string;
  date?: string;
  relatedPosts?: RelatedPost[];
  sidebarCta?: {
    text?: string;
    buttonLabel?: string;
    href?: string;
  };
  faq?: Array<{ question: string; answer: string }>;
}

export default function BlogPostTemplate({ blogData }: { blogData: BlogPostData }) {
  const pathname = usePathname();
  const publishedDate = formatDisplayDate(
    blogData?.hero?.datePublished || blogData?.datePublished || blogData?.date
  );

  const articleHtml = renderArticleHtml(blogData?.content || "", blogData?.internalLinks || []);
  const category = blogData?.hero?.category || "Article";
  const categorySlug = category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const recommendedCourses = (blogData?.internalLinks || [])
    .filter((item) => item?.slug?.startsWith("/courses/"))
    .slice(0, 2)
    .map((item) => ({
      title: item.label || "",
      slug: item.slug || "",
      duration: "Flexible Duration",
      level: "All Levels",
    }));

  return (
    <>
      <SeoHead
        currentPath={pathname || "/blog"}
        title={blogData?.seo?.title || blogData?.hero?.title || "Blog"}
        description={blogData?.seo?.description}
        keywords={blogData?.seo?.focusKeyword}
        canonical={`https://seasoftclasses.com${pathname ?? "/blog"}`}
        schema={[
          generateBlogPostSchema({
            title: blogData?.seo?.title || blogData?.hero?.title,
            description: blogData?.seo?.description,
            slug: pathname?.replace("/blog/", "") ?? "",
            image: blogData?.hero?.image,
            datePublished: blogData?.hero?.datePublished,
          }),
          ...(Array.isArray(blogData?.faq) && blogData.faq.length > 0
            ? [generateFAQSchema(blogData.faq)]
            : []),
        ]}
      />

      <main className="bg-white min-h-screen">
        <BreadcrumbWidget
          crumbs={[
            { label: "Home", href: "/" },
            { label: "Blog", href: "/blog" },
            { label: category, href: `/blog/category/${categorySlug || "general"}` },
            { label: blogData?.hero?.title || "Post", href: "" },
          ]}
        />

        <section className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 pt-10 md:pt-14">
          <div className="mb-10 md:mb-12 text-left">
            <span className="bg-[#EBEFFF] text-[#1B61FF] text-[10px] font-bold px-3 py-1 rounded uppercase tracking-wider">
              {blogData?.hero?.category || "Blog"}
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#1A1A1A] mt-4 mb-6 leading-tight max-w-4xl">
              {blogData?.hero?.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-[#666666] text-[16px]">
              <span>{blogData?.author || "Seasoft Classes Editorial Team"}</span>
              {publishedDate ? <span className="w-1 h-1 bg-gray-300 rounded-full" /> : null}
              {publishedDate ? <span>{publishedDate}</span> : null}
              <span className="w-1 h-1 bg-gray-300 rounded-full" />
              <span>{blogData?.hero?.readTime || "5 min read"}</span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 pb-20">
            <article className="flex-1 min-w-0">
              <div
                className="prose prose-lg max-w-none text-[#4B5563]"
                dangerouslySetInnerHTML={{ __html: articleHtml }}
              />

              <div className="mt-12 pt-8 border-t border-gray-100 bg-[#F9FAFB] rounded-2xl p-6 md:p-8">
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Written By</p>
                <p className="text-[#1A1A1A] text-lg font-extrabold">{blogData?.author || "Seasoft Classes Editorial Team"}</p>
              </div>
            </article>

            <aside className="lg:w-[360px] w-full">
              <div className="sticky top-24 space-y-6">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-[#1A1A1A] text-[20px] font-extrabold mb-4">Categories</h3>
                  <ul className="space-y-3 text-[18px]">
                    {BLOG_CATEGORIES.map((item) => (
                      <li key={item.href}>
                        <Link prefetch={true} href={item.href} className="text-[#4B5563] hover:text-[#1B61FF] transition-colors font-medium">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <p className="text-[#1A1A1A] text-base font-bold leading-relaxed mb-4">
                    {blogData?.sidebarCta?.text || "Ready to start your IT career journey?"}
                  </p>
                  <Link
                    prefetch={true}
                    href={blogData?.sidebarCta?.href || "/contact-us"}
                    onClick={() => trackEvent("Lead", "click", "counseling_cta", pathname || "/blog")}
                    className="w-full inline-flex items-center justify-center rounded-lg bg-[#1B61FF] text-white font-bold py-3 px-4 hover:bg-blue-600 transition-colors"
                  >
                    {blogData?.sidebarCta?.buttonLabel || "Talk to an Advisor"}
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <SocialShareWidget
          url={`https://seasoftclasses.com${pathname ?? "/blog"}`}
          title={blogData?.seo?.title || blogData?.hero?.title || "Blog"}
          description={blogData?.seo?.description || ""}
        />

        <section className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="bg-[#f0fdf4] border border-[#25D366] rounded-2xl p-6 my-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-800 text-lg">Interested in this topic?</h3>
              <p className="text-gray-600 text-[16px] mt-1">
                Chat with our IT advisors on WhatsApp - we&apos;ll recommend the right course for your goal.
              </p>
            </div>
            <WhatsAppButton
              variant="solid"
              size="lg"
              label="Chat with an Advisor"
              message={whatsappMessages.blog(blogData?.hero?.title || "this topic")}
              context="blog_post_cta"
              className="flex-shrink-0"
            />
            <EnrollButton
              label="Enroll in Related Course"
              variant="primary"
              size="md"
              context={FORM_CONTEXTS.BLOG_CTA}
              className="flex-shrink-0"
            />
          </div>
        </section>

        <RelatedContentWidget
          title="Continue Learning"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          blogs={(blogData?.relatedPosts || []) as any}
          courses={recommendedCourses}
        />
      </main>
    </>
  );
}

